// External Imports
import { useEffect, useRef, useState } from "react";
import {
  MdAdd,
  MdOutlineImageNotSupported,
} from "react-icons/md";

// Internal Imports
import { validateImageFile } from "../../util/validate-file";

/** ========================================================================
 * * ImageSelector Component
 * This component renders an image thumbnail which can be used as an input.
 * ========================================================================== */

export default function ImageSelector({
  image,
  name,
  label,
  edit = false,
  classes,
  imageClasses,
  children,
  loading,
}) {
  const [selectedImage, setSelectedImage] = useState(image);
  const [error, setError] = useState("");
  const imageRef = useRef();

  /* ======================  SELECT IMAGE HANDLER ====================== */
  const selectImage = (e) => {
    const file = e.target.files[0];

    // If no file selected, clear preview and error
    if (!file) {
      setSelectedImage(image);
      setError("");
      return;
    }

    // Validate image file type (PNG, JPG, HEIC)
    const { isValid, error: validationError } = validateImageFile(
      file,
      label || "Image"
    );

    if (!isValid) {
      // Reset input so the invalid file is not submitted
      e.target.value = "";
      setSelectedImage(null);
      setError(validationError);
      return;
    }

    setError("");
    const fileUrl = URL.createObjectURL(file);
    setSelectedImage(fileUrl);
  };

  /* ======================  CLICK HANDLER ====================== */
  const clickHandler = () => {
    if (edit) imageRef.current.click();
  };

  // Reset selected image if edit is cancelled
  useEffect(() => {
    setSelectedImage(image);
    setError("");
  }, [edit, image]);

  if (loading)
    return (
      <div
        className={`${classes || "h-48 w-72"
          } bg-stone-700 animate-pulse rounded-lg`}
      />
    );

  return (
    <div className={classes || "h-48 w-72"}>
      <input
        className="hidden"
        name={name || "image"}
        id={name || "image"}
        ref={imageRef}
        type="file"
        accept=".png,.webp,.PNG,.WEBP,image/png,image/webp"
        onChange={selectImage}
      />
      {selectedImage ? (
        <img
          onClick={clickHandler}
          src={selectedImage}
          className={`${imageClasses ? imageClasses : "w-full h-full object-contain"
            } cursor-pointer rounded-md`}
        />
      ) : (
        <div
          onClick={clickHandler}
          className="flex items-center justify-center w-full h-full border rounded-lg cursor-pointer border-stone-400 hover:opacity-80"
        >
          <div className="grid justify-items-center">
            <p className="text-stone-200">{label}</p>
            {edit ? (
              <MdAdd className="w-10 h-10 text-stone-200" />
            ) : (
              children || (
                <MdOutlineImageNotSupported className="w-8 h-8 text-stone-200" />
              )
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}
